/**
 * SEO Metadata Configuration
 *
 * Centralized SEO metadata for all pages.
 * Generated by AI with SEO best practices.
 */

import { SEASON_CONFIG } from './season';

const APPLICATIONS_OPEN = SEASON_CONFIG.application.status.message;

export interface PageSEO {
  title: string;
  metaDescription: string;
  keywords: string[];
  openGraph: {
    title: string;
    description: string;
  };
}

export const SEO_METADATA: Record<string, PageSEO> = {
  home: {
    title: "Atlantic Music Festival | Summer Classical Music Institute",
    metaDescription: `Atlantic Music Festival presents classical music performances, masterclasses, and summer programs. 700+ premieres, free concerts, renowned faculty. ${APPLICATIONS_OPEN}`,
    keywords: [
      "Atlantic Music Festival",
      "classical music festival Maine",
      "summer music program",
      "classical music performances",
      "music education Maine",
      "orchestra concerts",
      "chamber music festival",
      "music masterclasses"
    ],
    openGraph: {
      title: "Atlantic Music Festival - Where Extraordinary Musical Talent Gathers",
      description: "Experience six weeks of exceptional classical music performances, intensive training programs, and artistic collaboration at the Atlantic Music Festival. Free concerts featuring world-renowned musicians, emerging talents, and 700+ world premieres. Join us for an unforgettable musical journey."
    }
  },
  about: {
    title: "About Atlantic Music Festival | Our Mission & Impact",
    metaDescription: "A sanctuary for artistic freedom. 700+ premieres, world-renowned faculty, and transformative musical experiences. Learn about our mission to shape classical music's future.",
    keywords: [
      "about Atlantic Music Festival",
      "classical music Maine",
      "music festival mission",
      "artistic freedom music",
      "contemporary classical music",
      "new music premieres",
      "music education excellence",
      "Maine cultural enrichment"
    ],
    openGraph: {
      title: "About Atlantic Music Festival - A Sanctuary for Artistic Freedom & Musical Innovation",
      description: "Discover how Atlantic Music Festival has become a beacon of innovation in classical music. With over 700 world premieres, thousands of participants including 500 fellowship recipients, and collaborations with world-renowned musicians, we provide authentic musical experiences while enriching Maine's cultural landscape. Join our community where creativity knows no bounds."
    }
  },
  concerts: {
    title: "Classical Music Concerts | Atlantic Music Festival",
    metaDescription: "Discover free classical music concerts featuring world-class orchestras, chamber ensembles, and soloists. Browse our summer concert season.",
    keywords: [
      "classical music concerts Maine",
      "free concerts Maine",
      "orchestra performances",
      "chamber music concerts",
      "summer concert series",
      "live classical music",
      "Maine music events",
      "professional musicians concerts"
    ],
    openGraph: {
      title: "Summer Classical Music Concerts - Free Performances by World-Class Musicians",
      description: "Experience exceptional classical music in intimate concert settings. Our summer season features orchestral masterworks, intimate chamber music, solo recitals, and world premieres - all free and open to the public. Browse upcoming concerts featuring renowned artists and emerging talents from around the world."
    }
  },
  artists: {
    title: "World-Renowned Artists & Faculty | Atlantic Music Festival",
    metaDescription: "Meet our distinguished faculty and guest artists - internationally acclaimed musicians from top orchestras and conservatories. Browse by instrument and discover their expertise.",
    keywords: [
      "classical music faculty",
      "world-renowned musicians",
      "professional musicians Maine",
      "music teachers masterclass",
      "orchestra musicians",
      "chamber music artists",
      "conservatory faculty",
      "international music artists"
    ],
    openGraph: {
      title: "Distinguished Faculty & Guest Artists - Learn from the World's Leading Musicians",
      description: "Our artist-faculty includes members of major symphony orchestras, Grammy winners, and professors from leading conservatories worldwide. Browse our extensive roster of internationally acclaimed musicians who bring decades of performance experience and pedagogical expertise to Atlantic Music Festival. Filter by instrument to find your ideal mentors."
    }
  },
  instrumental: {
    title: "Instrumental Program | Atlantic Music Festival",
    metaDescription: "Intensive 6-week summer program for strings, winds, brass, and percussion. Private lessons, masterclasses, orchestra, and chamber music. Scholarships available. Apply today!",
    keywords: [
      "instrumental music program",
      "summer music institute",
      "orchestra training program",
      "chamber music program",
      "music masterclasses Maine",
      "private music lessons",
      "music education intensive",
      "conservatory summer program"
    ],
    openGraph: {
      title: "Instrumental Program - Transform Your Musicianship in Six Weeks",
      description: "An immersive summer music experience for advanced instrumentalists. Study with world-renowned faculty through private lessons and masterclasses. Perform in professional orchestras and chamber ensembles. Available for strings, woodwinds, brass, percussion, and harp. Fellowships and scholarships available. Perfect for conservatory students and emerging professionals seeking intensive musical growth."
    }
  },
  piano: {
    title: "Piano Institute & Seminar | Advanced Summer Piano Program",
    metaDescription: "Comprehensive 6-week piano program with distinguished faculty. Solo recitals, masterclasses, chamber music, concerto opportunities. One or two 3-week sessions available.",
    keywords: [
      "piano summer program",
      "piano institute Maine",
      "piano masterclasses",
      "advanced piano training",
      "piano recital opportunities",
      "conservatory piano program",
      "professional piano development",
      "piano chamber music"
    ],
    openGraph: {
      title: "Piano Institute & Seminar - Elevate Your Artistry with Master Pianists",
      description: "A comprehensive summer program designed for conservatory-level pianists seeking intensive artistic development. Study with distinguished international faculty in private lessons and masterclasses. Multiple performance opportunities including solo recitals, chamber collaborations, and concerto performances - all professionally recorded for your portfolio. Choose one 3-week session or the complete 6-week immersive experience."
    }
  },
  vocal: {
    title: "Vocal Institute | Summer Opera & Voice Training Program",
    metaDescription: "Elite summer vocal program for singers. Opera scenes, art song, masterclasses with renowned faculty. Performance opportunities and professional development.",
    keywords: [
      "vocal music program",
      "opera training Maine",
      "voice masterclasses",
      "summer vocal institute",
      "art song program",
      "opera scenes training",
      "classical voice training",
      "professional singer development"
    ],
    openGraph: {
      title: "Vocal Arts Institute - Opera & Art Song Training with World-Class Faculty",
      description: "An intensive summer program for advanced vocalists and collaborative pianists. Study opera repertoire, art song, and performance practice with internationally acclaimed vocal faculty and coaches. Participate in fully-staged opera scenes, recitals, and masterclasses. Develop your artistry through personalized coaching while building lasting connections with fellow singers and pianists."
    }
  },
  composition: {
    title: "Composition Program | New Music & Contemporary Composition",
    metaDescription: "Study composition with renowned composers. World premiere opportunities, readings with professional musicians, electronic music lab. Join 700+ premieres at AMF.",
    keywords: [
      "composition program",
      "new music composition",
      "contemporary classical music",
      "composition masterclasses",
      "music premiere opportunities",
      "composer residency",
      "electronic music program",
      "composition mentorship"
    ],
    openGraph: {
      title: "Composition Program - Create Tomorrow's Masterpieces with Leading Composers",
      description: "Join over 700 composers who have premiered works at Atlantic Music Festival. Study with internationally recognized composition faculty in an environment that champions new music and artistic innovation. Your works will receive professional readings and performances by world-class musicians. Access cutting-edge technology in our electronic music lab and collaborate across disciplines. A unique opportunity to develop your compositional voice in a supportive, creatively-charged summer environment."
    }
  },
  fees: {
    title: "Program Fees & Application Deadlines | Atlantic Music Festival",
    metaDescription: `Tuition, application fees, and program information for the Atlantic Music Festival. Figures shown are from our most recent season. ${APPLICATIONS_OPEN}`,
    keywords: [
      "music program tuition",
      "summer program fees",
      "application deadlines music",
      "music festival dates",
      "music scholarship information",
      "fellowship opportunities",
      "music program housing",
      "music institute costs"
    ],
    openGraph: {
      title: "Fees, Dates & Deadlines - Plan Your Summer at Atlantic Music Festival",
      description: "Complete information about application deadlines, program dates, tuition fees, and payment schedules for Atlantic Music Festival. Tuition includes housing, meals, and all program activities. Multiple fellowship and scholarship opportunities available for qualified applicants. Early application recommended for maximum financial aid consideration. Review session dates and plan your summer with us."
    }
  },
  contact: {
    title: "Contact Atlantic Music Festival | Get In Touch",
    metaDescription: "Questions about programs, auditions, or performances? Contact our admissions and festival offices. Email, phone, and mailing address information available.",
    keywords: [
      "contact music festival",
      "music program inquiries",
      "admissions contact",
      "festival information",
      "music program questions",
      "audition information",
      "Maine music festival contact",
      "program support"
    ],
    openGraph: {
      title: "Contact Us - We're Here to Answer Your Questions",
      description: "Get in touch with Atlantic Music Festival for inquiries about our programs, performances, auditions, or general information. Our admissions and festival offices are ready to help you navigate your musical journey. Whether you're a prospective participant, patron, or concert-goer, we'd love to hear from you. Find phone numbers, email addresses, and office hours for quick assistance."
    }
  }
};